import { useState, useEffect } from "react";

// MyErba brand mark (infinity + wordmark). We render on the same tinted bg as the splash
// with mix-blend-multiply so the logo's near-white background blends seamlessly into the scene.
const MYERBA_LOGO = "/api/files/branding/myerba.png";
// Erba Mannheim | Transasia combined mark
const ERBA_TRANSASIA_LOGO = "/api/files/branding/erba_transasia.png";

const MIN_DURATION_MS = 5000; // Splash must be visible for at least 5 seconds

export default function SplashScreen({ onComplete }) {
  // Read sessionStorage synchronously so splash never flashes when already seen.
  const alreadySeen = (() => { try { return sessionStorage.getItem("splash_seen") === "1"; } catch { return false; } })();
  const [phase, setPhase] = useState(alreadySeen ? 2 : 0);

  useEffect(() => {
    if (alreadySeen) { onComplete(); return; }
    const t1 = setTimeout(() => setPhase(1), 900);                    // content reveal
    const t2 = setTimeout(() => setPhase(2), MIN_DURATION_MS - 400);   // start fade-out
    const t3 = setTimeout(() => {
      try { sessionStorage.setItem("splash_seen", "1"); } catch { /* ignore */ }
      onComplete();
    }, MIN_DURATION_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete, alreadySeen]);

  if (alreadySeen) return null;

  return (
    <div
      className={`fixed inset-0 z-[200] flex flex-col items-center justify-center transition-opacity duration-500 ${phase === 2 ? "opacity-0" : "opacity-100"}`}
      style={{ background: "radial-gradient(circle at 50% 40%, #ffffff 0%, #eaf4f7 60%, #d7e8ef 100%)" }}
      data-testid="splash-screen"
    >
      {/* MyErba Logo — clean, no surrounding ring */}
      <div
        className={`transition-all duration-[900ms] ease-out ${phase >= 0 ? "scale-100 opacity-100 translate-y-0" : "scale-50 opacity-0 translate-y-6"}`}
        style={{ animation: phase === 0 ? "splashBounce 900ms cubic-bezier(0.34, 1.56, 0.64, 1) both" : "none" }}
      >
        <img
          src={MYERBA_LOGO}
          alt="MyErba"
          className="w-52 h-52 object-contain"
          data-testid="splash-logo"
        />
      </div>

      {/* Tagline removed per brand request */}

      {/* Erba Transasia sub-logo — "Loyalty Program by Erba Transasia" */}
      <div className={`mt-6 flex flex-col items-center transition-all duration-700 ${phase >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} data-testid="splash-sublogo">
        <p className="text-[9px] text-gray-500 tracking-[0.25em] uppercase mb-2">Loyalty Program by</p>
        <img
          src={ERBA_TRANSASIA_LOGO}
          alt="Erba Transasia"
          className="h-10 w-auto object-contain"
        />
      </div>

      {/* Powered by */}
      <div className={`absolute bottom-10 transition-all duration-700 ${phase >= 1 ? "opacity-60" : "opacity-0"}`}>
        <p className="text-[9px] text-gray-500 uppercase tracking-[0.25em]">Powered by TagNPay</p>
      </div>

      <style>{`
        @keyframes splashBounce {
          0%   { opacity: 0; transform: scale(0.4) translateY(16px); }
          60%  { opacity: 1; transform: scale(1.08) translateY(-4px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
